// BooLume fog popup: gamified email capture with Klaviyo and discount auto-apply.
import { pickReward } from 'bl-offers';
import { getConfig } from 'bl-config';
import { getStorage, safeGet, safeSet, maskCode, isSnoozed, prefersReducedMotion } from 'bl-util';
import { burst } from 'bl-motion';

const KLAVIYO_REVISION = '2026-07-15';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export async function subscribeKlaviyo(klaviyo, email, reward) {
  if (!klaviyo || !klaviyo.publicKey || !klaviyo.listId) return false;
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'BooLume fog popup',
        profile: { data: { type: 'profile', attributes: {
          email,
          properties: { boo_reward: reward.label, boo_code: reward.code },
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
        } } },
      },
      relationships: { list: { data: { type: 'list', id: klaviyo.listId } } },
    },
  };
  try {
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(klaviyo.publicKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch (e) {
    return false;
  }
}

export async function applyDiscount(code) {
  let ok = false;
  try {
    const r = await fetch(`/discount/${encodeURIComponent(code)}?redirect=%2Fcart.js`, { credentials: 'same-origin' });
    ok = r.ok;
  } catch (e) {}
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    if (cart.item_count > 0) {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      ok = ok || res.ok;
    }
  } catch (e) {}
  return ok;
}

class BlFogPopup extends HTMLElement {
  connectedCallback() {
    const cfg = getConfig();
    this.cfg = cfg.popup || {};
    if (!this.cfg.enabled || (cfg.designMode && !this.cfg.inEditor)) return;
    this.local = getStorage('local');
    this.launcher = document.querySelector('.bl-launcher');
    this.panel = this.querySelector('[role="dialog"]');
    this.canvas = this.querySelector('canvas');
    this.form = this.querySelector('form');
    this.msg = this.querySelector('.bl-pop__msg');
    this.revealed = false;
    this.busy = false;
    this.claimed = this.readClaim();
    this.bind();
    // The launcher appears only for visitors who closed the popup or already claimed a code.
    if (this.claimed || isSnoozed(Date.now(), safeGet(this.local, 'bl_treat_snooze'), this.cfg.snoozeDays)) {
      this.showLauncher();
      return;
    }
    this.arm();
  }

  readClaim() {
    try {
      return JSON.parse(safeGet(this.local, 'bl_treat_v1') || 'null');
    } catch (e) {
      return null;
    }
  }

  arm() {
    const fire = () => this.tryOpen();
    this.timer = setTimeout(fire, this.cfg.delayMs);
    this.onScroll = () => {
      const max = document.documentElement.scrollHeight - innerHeight;
      if (max > 0 && scrollY / max >= this.cfg.scrollRatio) fire();
    };
    addEventListener('scroll', this.onScroll, { passive: true });
    if (matchMedia('(hover: hover)').matches) {
      this.onLeave = (e) => { if (!e.relatedTarget && e.clientY <= 0) fire(); };
      document.addEventListener('mouseout', this.onLeave);
    }
  }

  disarm() {
    clearTimeout(this.timer);
    removeEventListener('scroll', this.onScroll);
    if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
  }

  tryOpen() {
    this.disarm();
    const busyPath = /^\/(cart|checkouts)/.test(location.pathname);
    const drawerOpen = document.querySelector('cart-drawer.active');
    if (busyPath || drawerOpen) return;
    this.open();
  }

  open() {
    if (!this.hidden) return;
    this.lastFocus = document.activeElement;
    if (this.claimed) {
      this.showDone(this.claimed.code);
    } else {
      this.reward = this.reward || pickReward(this.cfg.rewards);
      if (!this.reward) return;
      this.querySelector('[data-prize-label]').textContent = this.reward.label;
      this.querySelector('[data-prize-code]').textContent = maskCode(this.reward.code);
    }
    this.hidden = false;
    document.body.classList.add('overflow-hidden');
    if (!this.claimed && !this.revealed) requestAnimationFrame(() => this.paintFog());
    (this.querySelector('[data-reveal]:not([hidden])') || this.querySelector('[data-close]')).focus();
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    document.body.classList.remove('overflow-hidden');
    if (!this.claimed) safeSet(this.local, 'bl_treat_snooze', String(Date.now()));
    this.showLauncher();
    (this.lastFocus && this.lastFocus.focus ? this.lastFocus : this.launcher)?.focus();
  }

  showLauncher() {
    if (!this.launcher) return;
    const text = this.launcher.querySelector('[data-launcher-text]');
    if (this.claimed) text.textContent = window.blStrings.popup.launcherCode.replace('[code]', this.claimed.code);
    this.launcher.hidden = false;
  }

  bind() {
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]')) this.close();
      if (e.target.closest('[data-reveal]')) this.reveal();
      if (e.target.closest('[data-copy]')) this.copy();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trap(e);
    });
    this.launcher?.addEventListener('click', () => this.open());
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
    let drawing = false;
    let last = null;
    const pos = (e) => { const r = this.canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    this.canvas.addEventListener('pointerdown', (e) => { drawing = true; last = null; this.canvas.setPointerCapture(e.pointerId); this.erase(pos(e), last); last = pos(e); });
    this.canvas.addEventListener('pointermove', (e) => { if (!drawing) return; const p = pos(e); this.erase(p, last); last = p; });
    const end = () => { drawing = false; last = null; this.checkCleared(); };
    this.canvas.addEventListener('pointerup', end);
    this.canvas.addEventListener('pointercancel', end);
  }

  trap(e) {
    const f = [...this.panel.querySelectorAll('button, input, [href]')].filter((el) => !el.disabled && el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  paintFog() {
    const c = this.canvas;
    const r = c.getBoundingClientRect();
    const dpr = Math.min(2, devicePixelRatio || 1);
    c.width = Math.round(r.width * dpr);
    c.height = Math.round(r.height * dpr);
    const ctx = (this.ctx = c.getContext('2d'));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    const g = ctx.createLinearGradient(0, 0, r.width, r.height);
    g.addColorStop(0, '#6E5A86'); g.addColorStop(0.5, '#9A8AAE'); g.addColorStop(1, '#5B4873');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, r.width, r.height);
    for (let i = 0; i < 26; i++) {
      const x = Math.random() * r.width, y = Math.random() * r.height, rad = 30 + Math.random() * 70;
      const rg = ctx.createRadialGradient(x, y, 0, x, y, rad);
      rg.addColorStop(0, 'rgba(255,244,230,.28)'); rg.addColorStop(1, 'rgba(255,244,230,0)');
      ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(x, y, rad, 0, 7); ctx.fill();
    }
    ctx.fillStyle = 'rgba(18,12,28,.75)';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = "26px 'Bagel Fat One', 'Fredoka', sans-serif";
    ctx.fillText(window.blStrings.popup.fogText, r.width / 2, r.height / 2 - 8);
    ctx.font = "600 14px 'Fredoka', sans-serif";
    ctx.fillText(window.blStrings.popup.fogHint, r.width / 2, r.height / 2 + 20);
  }

  erase(p, last) {
    if (!this.ctx || this.revealed) return;
    const ctx = this.ctx;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = ctx.strokeStyle = '#000';
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = 46;
    ctx.beginPath();
    if (last) { ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke(); }
    else { ctx.arc(p.x, p.y, 23, 0, 7); ctx.fill(); }
  }

  checkCleared() {
    if (!this.ctx || this.revealed) return;
    const { width: w, height: h } = this.canvas;
    const d = this.ctx.getImageData(0, 0, w, h).data;
    let clear = 0, total = 0;
    for (let i = 3; i < d.length; i += 4 * 24) { total++; if (d[i] < 40) clear++; }
    if (clear / total > 0.45) this.reveal();
  }

  reveal() {
    if (this.revealed) return;
    this.revealed = true;
    this.canvas.classList.add('is-gone');
    this.querySelector('[data-reveal]').hidden = true;
    const r = this.querySelector('.bl-pop__scratch').getBoundingClientRect();
    if (!prefersReducedMotion()) burst(r.left + r.width / 2, r.top + r.height / 2, 26);
    this.querySelector('#bl-pop-email').focus();
  }

  async submit() {
    if (this.busy) return;
    const s = window.blStrings.popup;
    if (!this.revealed) { this.msg.textContent = s.revealFirst; return; }
    const email = this.form.email.value.trim();
    if (!EMAIL_RE.test(email)) { this.msg.textContent = s.invalidEmail; return; }
    this.busy = true;
    this.form.querySelector('button[type=submit]').disabled = true;
    this.msg.textContent = '';
    const saved = await subscribeKlaviyo(this.cfg.klaviyo, email, this.reward);
    await applyDiscount(this.reward.code);
    this.claimed = { code: this.reward.code, label: this.reward.label, ts: Date.now() };
    safeSet(this.local, 'bl_treat_v1', JSON.stringify(this.claimed));
    this.showDone(this.reward.code);
    if (!saved) this.msg.textContent = s.emailFailed;
    this.showLauncher();
  }

  showDone(code) {
    this.querySelector('.bl-pop__scratch').hidden = true;
    this.querySelector('[data-reveal]').hidden = true;
    this.form.querySelectorAll('input, button, label, .bl-pop__consent').forEach((el) => (el.hidden = true));
    this.querySelector('.bl-pop__done').hidden = false;
    this.querySelector('[data-final-code]').textContent = code;
  }

  async copy() {
    const code = this.querySelector('[data-final-code]').textContent;
    try {
      await navigator.clipboard.writeText(code);
      this.querySelector('[data-copy]').textContent = window.blStrings.popup.copied;
    } catch (e) {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-final-code]'));
      getSelection().removeAllRanges();
      getSelection().addRange(range);
    }
  }
}
if (!customElements.get('bl-fog-popup')) customElements.define('bl-fog-popup', BlFogPopup);
